import React, { useState } from 'react';
import { X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import api from '../../services/api';
import { WORLD_COUNTRIES, RESIDENCE_PRIORITY, NATIONALITY_PRIORITY } from '../../data/countries';

const SELF_DESCRIPTION_OPTIONS = [
  'Diaspora professional',
  'Diaspora family office',
  'Expatriate investor',
  'Expatriate family office',
  'Other',
];

// Build select options: priority countries first, separator, then remaining alphabetically
function buildCountryOptions(priorityNames) {
  const prioritySet = new Set(priorityNames);
  const priorityCountries = priorityNames
    .map(name => WORLD_COUNTRIES.find(c => c.name === name))
    .filter(Boolean);
  const rest = WORLD_COUNTRIES.filter(c => !prioritySet.has(c.name));
  return { priorityCountries, rest };
}

const residenceOptions = buildCountryOptions(RESIDENCE_PRIORITY);
const nationalityOptions = buildCountryOptions(NATIONALITY_PRIORITY);

const DemoRegistrationModal = ({ onClose, demoParams }) => {
  const [step, setStep] = useState(1);

  // Form fields
  const [countryOfResidence, setCountryOfResidence] = useState('');
  const [nationality, setNationality] = useState('');
  const [email, setEmail] = useState('');
  const [selfDescription, setSelfDescription] = useState('');
  const [selfDescriptionOther, setSelfDescriptionOther] = useState('');
  const [waitlistOptedIn, setWaitlistOptedIn] = useState(false);

  // Submission state
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [subscriberId, setSubscriberId] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [effectiveWaitlistOptedIn, setEffectiveWaitlistOptedIn] = useState(false);

  // Referral state
  const [referralEmail, setReferralEmail] = useState('');
  const [referralStatus, setReferralStatus] = useState('idle');

  const validateEmail = (em) =>
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(em);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    if (!countryOfResidence) { setStatus('error'); setErrorMessage('Please select your country of residence.'); return; }
    if (!nationality) { setStatus('error'); setErrorMessage('Please select your nationality.'); return; }
    if (!email.trim()) { setStatus('error'); setErrorMessage('Please enter your email address.'); return; }
    if (!validateEmail(email)) { setStatus('error'); setErrorMessage('Please enter a valid email address.'); return; }
    if (!selfDescription) { setStatus('error'); setErrorMessage('Please select how you would describe yourself.'); return; }
    if (selfDescription === 'Other' && !selfDescriptionOther.trim()) { setStatus('error'); setErrorMessage('Please specify your description.'); return; }

    setStatus('loading');
    setErrorMessage('');

    try {
      const params = new URLSearchParams(window.location.search);
      const result = await api.submitDemoRegistration({
        email: email.toLowerCase().trim(),
        country_of_residence: countryOfResidence,
        nationality,
        self_description: selfDescription,
        self_description_other: selfDescription === 'Other' ? selfDescriptionOther.trim() : undefined,
        waitlist_opted_in: waitlistOptedIn,
        demo_params: demoParams,
        utm_source: params.get('utm_source') || undefined,
        utm_medium: params.get('utm_medium') || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
      });

      setAlreadyRegistered(result.already_registered || false);
      setSubscriberId(result.subscriberId || null);
      setEmailSent(result.email_sent !== false);
      setEffectiveWaitlistOptedIn(result.waitlist_opted_in || false);
      // Cache demo params locally so the email link can auto-run the demo
      // even if the backend URL doesn't include inline params
      if (demoParams) {
        try { localStorage.setItem('ff_demo_params', JSON.stringify(demoParams)); } catch { /* ignore */ }
      }
      setStatus('success');
      setStep(2);
    } catch (err) {
      console.error('Demo registration error:', err);
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  const labelClass = 'font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1 block';
  const selectClass = 'w-full font-mono text-xs border border-zinc-300 bg-white px-3 py-2 text-black focus:outline-none focus:border-[#4D4D4D] transition-colors';
  const inputClass = `w-full px-4 py-3 border text-black placeholder:text-zinc-400 text-xs font-mono bg-transparent focus:outline-none focus:border-[#4D4D4D] transition-colors ${
    status === 'error' ? 'border-zinc-300' : 'border-zinc-300'
  }`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white border border-zinc-300 p-5 sm:p-8 max-w-md w-full relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-400 hover:text-black transition-colors">
          <X className="w-5 h-5" />
        </button>

        {/* ──── Step 1: Registration Form ──── */}
        {step === 1 && (
          <>
            <h3 className="font-sans text-xl font-bold text-black mb-2">Register to View Demo</h3>
            <p className="text-sm text-zinc-600 font-sans mb-6">
              Complete the form below. We will email you a link to view your personalized demo results.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Country of Residence */}
              <div>
                <label className={labelClass}>Country of Residence</label>
                <select
                  value={countryOfResidence}
                  onChange={e => setCountryOfResidence(e.target.value)}
                  className={selectClass}
                >
                  <option value="">Select country...</option>
                  {residenceOptions.priorityCountries.map(c => (
                    <option key={c.code} value={c.name}>{c.name}</option>
                  ))}
                  <option disabled>────────────</option>
                  {residenceOptions.rest.map(c => (
                    <option key={c.code} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Nationality */}
              <div>
                <label className={labelClass}>Nationality</label>
                <select
                  value={nationality}
                  onChange={e => setNationality(e.target.value)}
                  className={selectClass}
                >
                  <option value="">Select nationality...</option>
                  {nationalityOptions.priorityCountries.map(c => (
                    <option key={c.code} value={c.name}>{c.name}</option>
                  ))}
                  <option disabled>────────────</option>
                  {nationalityOptions.rest.map(c => (
                    <option key={c.code} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Email */}
              <div>
                <label className={labelClass}>Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={inputClass}
                  disabled={status === 'loading'}
                />
              </div>

              {/* Self Description */}
              <div>
                <label className={labelClass}>How would you describe yourself?</label>
                <select
                  value={selfDescription}
                  onChange={e => setSelfDescription(e.target.value)}
                  className={selectClass}
                >
                  <option value="">Select...</option>
                  {SELF_DESCRIPTION_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {/* Other description text input */}
              {selfDescription === 'Other' && (
                <div>
                  <label className={labelClass}>Please specify</label>
                  <input
                    type="text"
                    value={selfDescriptionOther}
                    onChange={e => setSelfDescriptionOther(e.target.value)}
                    placeholder="Describe yourself..."
                    className={inputClass}
                    disabled={status === 'loading'}
                  />
                </div>
              )}

              {/* Waitlist opt-in */}
              <label className="flex items-center gap-3 cursor-pointer py-1">
                <input
                  type="checkbox"
                  checked={waitlistOptedIn}
                  onChange={e => setWaitlistOptedIn(e.target.checked)}
                  className="w-4 h-4 accent-[#4D4D4D]"
                />
                <span className="text-xs font-sans text-zinc-600">Add me to launch waitlist (optional)</span>
              </label>

              {/* Error message */}
              {status === 'error' && errorMessage && (
                <p className="text-xs font-mono text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{errorMessage}
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full py-3 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] hover:bg-black transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Register for Demo'}
              </button>
            </form>
          </>
        )}

        {/* ──── Step 2: Thank You ──── */}
        {step === 2 && (
          <div className="text-center py-4">
            <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
            {alreadyRegistered ? (
              <>
                <h3 className="font-sans text-xl font-bold text-black mb-2">Welcome Back!</h3>
                {emailSent ? (
                  <p className="text-sm text-zinc-600 font-sans mb-1">
                    You're already registered. We've sent a fresh demo link to your email.
                  </p>
                ) : (
                  <p className="text-sm text-red-600 font-sans mb-1">
                    You're already registered but we couldn't send the demo link. Please try again shortly.
                  </p>
                )}
                {effectiveWaitlistOptedIn && (
                  <p className="text-sm text-zinc-600 font-sans">
                    You're on our launch waitlist.
                  </p>
                )}
                <div className="mt-4 p-3 bg-zinc-50 border border-zinc-200 text-left">
                  <p className="text-xs font-mono text-zinc-500 uppercase tracking-[0.15em] mb-2">
                    Know someone who'd benefit?
                  </p>
                  {referralStatus === 'sent' ? (
                    <div>
                      <p className="text-xs font-mono text-emerald-600 flex items-center gap-1 mb-2">
                        <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />Thank you for the referral!
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setReferralStatus('idle'); setReferralEmail(''); }}
                          className="flex-1 px-3 py-2 text-xs font-mono uppercase border border-zinc-300 hover:border-[#4D4D4D] transition-all"
                        >
                          Refer Another
                        </button>
                        <button
                          onClick={onClose}
                          className="flex-1 px-3 py-2 text-xs font-mono uppercase bg-[#4D4D4D] text-white hover:bg-black transition-all"
                        >
                          Proceed
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={referralEmail}
                          onChange={e => setReferralEmail(e.target.value)}
                          placeholder="friend@example.com"
                          className="flex-1 px-3 py-2 text-xs font-mono border border-zinc-300 bg-white text-zinc-700 focus:outline-none focus:border-[#4D4D4D] transition-colors"
                          disabled={referralStatus === 'loading'}
                        />
                        <button
                          onClick={async () => {
                            if (!referralEmail.trim() || !validateEmail(referralEmail)) {
                              setReferralStatus('error');
                              return;
                            }
                            setReferralStatus('loading');
                            try {
                              await api.submitReferral({ referrer_id: subscriberId, referral_email: referralEmail.toLowerCase().trim() });
                              setReferralStatus('sent');
                              setReferralEmail('');
                            } catch {
                              setReferralStatus('error');
                            }
                          }}
                          disabled={referralStatus === 'loading'}
                          className="px-3 py-2 text-xs font-mono uppercase border border-zinc-300 hover:border-[#4D4D4D] transition-all disabled:opacity-50 flex items-center gap-1"
                        >
                          {referralStatus === 'loading' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Refer'}
                        </button>
                      </div>
                      {referralStatus === 'error' && (
                        <p className="text-xs font-mono text-red-600 mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />Please enter a valid email address.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <h3 className="font-sans text-xl font-bold text-black mb-2">Thank You!</h3>
                <p className="text-sm text-zinc-600 font-sans mb-1">
                  You've been registered{effectiveWaitlistOptedIn ? ' and added to our launch waitlist' : ''}.
                </p>
                {emailSent ? (
                  <p className="text-sm text-zinc-600 font-sans">
                    Please check your email for your demo link.
                  </p>
                ) : (
                  <p className="text-sm text-red-600 font-sans">
                    We couldn't send your demo link email. Please try again shortly.
                  </p>
                )}
              </>
            )}
            <div className="mt-6 flex gap-3 justify-center">
              <button
                onClick={onClose}
                className="px-6 py-2 font-mono text-xs uppercase tracking-[0.15em] border border-zinc-300 text-black hover:border-[#4D4D4D] transition-all"
              >
                Close
              </button>
              <button
                onClick={onClose}
                className="px-6 py-2 font-mono text-xs uppercase tracking-[0.15em] bg-[#4D4D4D] text-white hover:bg-black transition-all"
              >
                Proceed
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoRegistrationModal;
